import React, { useCallback, useRef, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { PER_PAGE_OTHER } from '../../../../constants/common';
import { getTileWidgetListRepository } from '../../../../repository/mytm/shop/shopRepository';
import { TheDailyHeader } from '../../../organisms/the-daily/TheDailyHeader';
import { TheDailyTable } from '../../../organisms/the-daily/TheDailyTable';
import { SHOP_TYPE } from '../../../../types/shop';
import { FormTheDailyModal } from '../../../organisms/the-daily/FormTheDailyModal';
import { appStyles } from '../../../../styles';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { FormScheduleTileModal } from '../../../organisms/the-daily/FormScheduleTileModal';
import SelectButtons from '../../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../../hooks/useCountrySelection';

export const TheDaily = () => {
  const [pageActive, setPageActive] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
  const [record, setDataRecord] = useState<any>();
  const [scheduleRecord, setDataScheduleRecord] = useState<any>();
  const errorCount = useRef(0);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);

  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [pageActive, 1, countrySelected] : null,
    (pageNumber: number) =>
      getTileWidgetListRepository({
        page: pageNumber,
        take: PER_PAGE_OTHER,
        country: countrySelected!,
        type: SHOP_TYPE.THE_DAILY,
        sort: true,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);
  const onCloseScheduleModal = useCallback(() => {
    setIsOpenScheduleModal(false);
  }, []);

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
    window.scrollTo(0, 0);
  }, []);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const recordTheDaily = data?.widgets?.map((item: any, index: number) => ({ ...item, ...{ index } }));

  return (
    <Container>
      <TheDailyHeader setIsOpenModal={setIsOpenModal} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      {!data && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {data && (
        <TheDailyTable
          handleChangePageActive={handleChangePageActive}
          onRefresh={mutate}
          records={recordTheDaily || []}
          loading={loading || isValidating}
          setLoading={setLoading}
          setIsOpenModal={setIsOpenModal}
          setDataRecord={setDataRecord}
          setIsOpenScheduleModal={setIsOpenScheduleModal}
          setDataScheduleRecord={setDataScheduleRecord}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_OTHER,
            current: pageActive,
            total: data?.total,
          }}
        />
      )}

      {isOpenModal && countrySelected && (
        <FormTheDailyModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={mutate}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
          countrySelected={countrySelected}
        />
      )}
      {isOpenScheduleModal && (
        <FormScheduleTileModal
          setDataRecord={setDataScheduleRecord}
          record={scheduleRecord}
          isOpenModal={isOpenScheduleModal}
          onClose={onCloseScheduleModal}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
